import { useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { LogOut, MessageCircle, Settings, User as UserIcon, Users as UsersIcon } from "lucide-react";
import { cn } from "@/lib/cn";

interface AvatarMenuProps {
  email: string;
  initial: string;
  isAdmin: boolean;
  onSignOut?: () => void;
}

/**
 * Top-right circle avatar that opens a small menu with Profile +
 * (admin-only) Company / Users + Sign-out.
 */
export function AvatarMenu({ email, initial, isAdmin, onSignOut }: AvatarMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onOutside);
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={email}
        className="size-9 rounded-full bg-navy-700 text-white text-sm font-medium grid place-items-center hover:opacity-90"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {initial}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-30 min-w-[240px] rounded-lg border border-border bg-white shadow-lg overflow-hidden"
        >
          <div className="px-3 py-2.5 border-b border-border">
            <div className="text-sm font-medium text-navy-900 truncate">{email}</div>
            <div className="text-xs text-neutral-500 mt-0.5">{isAdmin ? "Admin" : "User"}</div>
          </div>
          <ul className="py-1">
            <MenuItem to="/profile" icon={<UserIcon className="size-4" />}>
              My profile
            </MenuItem>
            {isAdmin && (
              <>
                <MenuItem to="/company-profile" icon={<Settings className="size-4" />}>
                  Company settings
                </MenuItem>
                <MenuItem to="/settings/whatsapp" icon={<MessageCircle className="size-4" />}>
                  WhatsApp alerts
                </MenuItem>
                <MenuItem to="/admin/users" icon={<UsersIcon className="size-4" />}>
                  Manage users
                </MenuItem>
              </>
            )}
            {onSignOut && (
              <>
                <li className="border-t border-border my-1" />
                <li>
                  <button
                    type="button"
                    onClick={onSignOut}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-2 text-sm text-left",
                      "text-danger-700 hover:bg-danger-100/60",
                    )}
                  >
                    <LogOut className="size-4" />
                    Sign out
                  </button>
                </li>
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li>
      <Link
        to={to}
        role="menuitem"
        className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-navy-900"
        activeProps={{ className: "bg-navy-50 text-navy-900" }}
      >
        {icon}
        {children}
      </Link>
    </li>
  );
}
